/**
 * T516 — when a replayed offline write actually happened.
 *
 * `completeRitualSession` stamps `completedAt` with the server's clock. For a live
 * request those are the same moment; for a write that sat in IndexedDB overnight they
 * are not, and the difference is the member's week. She trains on Sunday with no
 * signal, reconnects on Monday, and the session she did on Sunday is recorded as
 * Monday's — which can cost her the week she had already earned. The comprehension
 * judge surfaced it as a member's own question: *"si el móvil no coge cobertura hasta
 * el lunes, ¿cuenta en esta semana o en la siguiente?"*
 *
 * The queue has always stored `queuedAt`; this is what makes it mean something.
 *
 * A CLIENT-SUPPLIED TIME IS UNTRUSTED, and that is the whole reason this is a function
 * rather than a `new Date(header)`. Unbounded, it would let anyone backfill sessions
 * into any past week and manufacture a streak. The bounds below are the trade: a value
 * outside them is not rejected — that would cost her the completion over a clock she
 * does not control — it simply falls back to the server's now, which is today's
 * behaviour.
 */

/**
 * How far back a replay may claim to have happened. A queue entry older than this is
 * past any plausible offline window, so its timestamp is not worth trusting.
 */
export const MAX_REPLAY_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Tolerance for a phone whose clock runs ahead. Anything beyond it is either a broken
 * clock or a forgery, and both resolve the same way.
 */
export const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

/**
 * @param rawQueuedAt the `X-Queued-At` header, epoch milliseconds as a string.
 * @param isReplay whether this request carried a valid idempotency key. A live request
 *   has no business claiming a past time, so the header is ignored outright there.
 * @returns the moment to record the completion at.
 */
export function resolveCompletedAt(
  rawQueuedAt: string | null,
  isReplay: boolean,
  now: Date,
): Date {
  if (!isReplay || rawQueuedAt === null) return now;

  const queuedAt = Number(rawQueuedAt);
  if (!Number.isInteger(queuedAt) || queuedAt <= 0) return now;

  const nowMs = now.getTime();
  if (queuedAt > nowMs + MAX_CLOCK_SKEW_MS) return now;
  if (queuedAt < nowMs - MAX_REPLAY_AGE_MS) return now;

  return new Date(queuedAt);
}
